import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiCheckbox,
  MiPlaceholderArea,
  MiPopper,
} from "@miplatform/components";
import { BUTTON_COLORS, BUTTON_VARIANTS } from "@miplatform/constants";
import { ICategoryDropsResponse, IDropTypeResponse } from "@miplatform/types";
import { Box, ClickAwayListener } from "@mui/material";

export function FloorsDropTypeSelect({
  anchorEl,
  open,
  dropTypes,
  categoryDrops,
  selectedDropTypes,
  appliedDropTypeIds,
  selectedCategoryId,
  setSelectedDropTypes,
  handleApplyDropTypes,
  handleCloseDropTypeSelect,
}: Readonly<DropTypeSelectProps>) {
  const { classes, theme } = useStyles();

  const noDropTypes = dropTypes.length === 0;

  const mappedDropTypes = dropTypes.map((dt) => {
    const hasCategoryDrop = categoryDrops.some(
      (cd) => cd.categoryId === selectedCategoryId && cd.dropTypeId === dt.id
    );
    const isApplied = appliedDropTypeIds.includes(dt.id);
    return { ...dt, isDisabled: !hasCategoryDrop || isApplied };
  });

  const toggleDropType = (dropTypeId: string) => {
    const isChecked = selectedDropTypes.includes(dropTypeId);
    const newSelectedDropTypes = isChecked
      ? selectedDropTypes.filter((id) => id !== dropTypeId)
      : [...selectedDropTypes, dropTypeId];
    setSelectedDropTypes(newSelectedDropTypes);
  };

  const handleApply = () => {
    if (selectedCategoryId) {
      handleApplyDropTypes();
    }
  };

  return (
    <MiPopper
      anchorEl={anchorEl}
      open={open}
      placement="bottom"
      sx={{
        boxShadow: theme.shadows[1],
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.common.white,
      }}
    >
      <ClickAwayListener onClickAway={handleCloseDropTypeSelect}>
        <Box>
          <Box className={classes.popperContent}>
            {noDropTypes && (
              <MiPlaceholderArea
                text="No Drop Types"
                bgColor={theme.palette.primary.light}
              />
            )}
            {mappedDropTypes.map((dt) => {
              return (
                <Box key={dt.id}>
                  <MiCheckbox
                    label={dt.name}
                    size="small"
                    value={selectedDropTypes.includes(dt.id)}
                    disabled={dt.isDisabled}
                    handleChange={() => toggleDropType(dt.id)}
                  />
                </Box>
              );
            })}
          </Box>
          <Box className={classes.popperActions}>
            <MiButton
              title="Apply"
              variant={BUTTON_VARIANTS.CONTAINED}
              size="small"
              onClick={handleApply}
              disabled={noDropTypes}
            />
            <MiButton
              title="Cancel"
              variant={BUTTON_VARIANTS.TEXT}
              size="small"
              className={classes.cancelButton}
              color={BUTTON_COLORS.BLACK}
              onClick={handleCloseDropTypeSelect}
            />
          </Box>
        </Box>
      </ClickAwayListener>
    </MiPopper>
  );
}

const useStyles = makeStyles()((theme) => ({
  popperContent: {
    padding: theme.spacing(2),
    maxHeight: 200,
    overflowY: "auto",
  },
  popperActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(1, 2),
    borderTop: ` 1px solid ${theme.palette.grey.light}`,
  },
  cancelButton: {
    textDecoration: "underline",
  },
}));

interface DropTypeSelectProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  dropTypes: Array<IDropTypeResponse>;
  categoryDrops: Array<ICategoryDropsResponse>;
  selectedDropTypes: string[];
  appliedDropTypeIds: string[];
  setSelectedDropTypes: (dropTypeIds: string[]) => void;
  selectedCategoryId: string | null;
  handleApplyDropTypes: () => void;
  handleCloseDropTypeSelect: () => void;
}
